import { InputLabel, Select, FormControl } from "@material-ui/core";
import { isNil } from "lodash";
import { usePage } from "@inertiajs/inertia-react";
import { useState } from "react";

import { ShoppingCart } from "./ShoppingCart";
import { useTransportProducts } from "../hooks/useTransportProducts";
import { Trans } from "../utils/i18n";
import { formatCurrency } from "../utils/formatCurrency";
import {
    calculateSubtotalPrice,
    calculateSummedVatPrice,
    calculateTotalPrice,
} from "../utils/priceCalculations";

export function ShoppingCartField({
    transportProductId,
    products = [],
    onChangeTransportProduct = () => {},
    onRemoveProduct = () => {},
    onUpdateProductQuantity = () => {},
    ...props
}) {
    const { maxQuantity = 20 } = usePage().props;
    const [handlingFeeProduct, setHandlingFeeProduct] = useState(null);
    const { data: transportProductsData } = useTransportProducts({
        products: products.map((product) => ({
            id: product.id,
            amount: Number(product.amount ?? 1),
        })),
        onSuccess: (data) => {
            // Set the handling fee product if it is available
            if (data?.handling) {
                setHandlingFeeProduct(data?.handling);
            }

            // If there are no transport products available due to amount changes, we need to reset the transport product.
            if (
                transportProduct &&
                (!data?.transport || data?.transport?.length === 0)
            ) {
                onChangeTransportProduct(null);
            }

            // If the transport product is not set yet, we set it as default to the first transport product
            if (!transportProduct && data?.transport?.[0]?.id) {
                onChangeTransportProduct(data?.transport?.[0]);
            }
        },
        // It's essential here to use a unique query key.
        // Otherwise, if we use this hook somewhere else, the data might be already cached and we are not getting
        // into the onSuccess callback.
        queryKey: ["shoppingCartField"]
    });
    const transportProduct = transportProductsData?.transport?.find(
        (product) => product.id == transportProductId
    );
    const subtotal = calculateSubtotalPrice(
        [...products, transportProduct, handlingFeeProduct]
            .filter(Boolean)
            .map((product) => ({
                amount: Number(product.amount ?? 1),
                netPrice: product.price?.price,
                grossPrice: product.price?.priceGross,
                tax: product.price?.vat,
            }))
    );
    const vat = calculateSummedVatPrice(
        [...products, transportProduct, handlingFeeProduct]
            .filter(Boolean)
            .map((product) => ({
                amount: Number(product.amount ?? 1),
                netPrice: product.price?.price,
                grossPrice: product.price?.priceGross,
                tax: product.price?.vat,
            }))
    );
    const total = calculateTotalPrice(
        [...products, transportProduct, handlingFeeProduct]
            .filter(Boolean)
            .map((product) => ({
                amount: Number(product.amount ?? 1),
                netPrice: product.price?.price,
                grossPrice: product.price?.priceGross,
                tax: product.price?.vat,
            }))
    );

    return (
        <ShoppingCart.Root {...props}>
            {products.map((product) => (
                <ShoppingCart.ProductRow
                    key={product.id}
                    image={product.image}
                    title={product.description}
                    quantity={product.amount}
                    maxQuantity={
                        maxQuantity ||
                        product.stockQuantity ||
                        product.stock_quantity
                    }
                    price={product.price}
                    showImage={true}
                    showQuantity={true}
                    showRemoveButton={true}
                    onRemove={() => onRemoveProduct(product)}
                    onUpdateQuantity={(amount) =>
                        onUpdateProductQuantity(product.id, amount)
                    }
                />
            ))}

            {handlingFeeProduct && (
                <ShoppingCart.ProductRow
                    title={handlingFeeProduct.description}
                    price={handlingFeeProduct.price}
                    quantity={1}
                    showImage={false}
                    showQuantity={false}
                    showRemoveButton={false}
                />
            )}

            {transportProduct && (
                <ShoppingCart.ProductRow
                    title={
                        <FormControl variant="outlined" size="small">
                            <InputLabel id="transport-label">
                                <Trans i18nKey="common.transport">
                                    Transport
                                </Trans>
                            </InputLabel>
                            <Select
                                className="w-full"
                                labelId="transport-label"
                                label={
                                    <Trans i18nKey="common.transport">
                                        Transport
                                    </Trans>
                                }
                                size="small"
                                onChange={(e) => {
                                    const transportProduct =
                                        transportProductsData?.transport?.find(
                                            (product) =>
                                                product.id == e.target.value
                                        );

                                    if (!transportProduct) return;

                                    onChangeTransportProduct(transportProduct);
                                }}
                                value={transportProductId}
                                native
                            >
                                {transportProductsData?.transport?.map(
                                    (product) => (
                                        <option
                                            key={product.id}
                                            value={product.id}
                                        >
                                            {product.description}
                                        </option>
                                    )
                                ) ?? []}
                            </Select>
                        </FormControl>
                    }
                    price={transportProduct.price}
                    quantity={1}
                    showImage={false}
                    showQuantity={false}
                    showRemoveButton={false}
                />
            )}

            <ShoppingCart.PriceSummaryRow
                title={<Trans i18nKey="common.subtotal">Subtotal</Trans>}
                price={formatCurrency(
                    subtotal,
                    transportProduct?.price?.currency ?? "EUR"
                )}
            />

            <ShoppingCart.PriceSummaryRow
                title={
                    <>
                        <Trans i18nKey="common.vat">VAT</Trans>{" "}
                        {!isNil(transportProduct?.price?.vat) &&
                            `(${transportProduct?.price?.vat / 100}%)`}
                    </>
                }
                price={formatCurrency(
                    vat,
                    transportProduct?.price?.currency ?? "EUR"
                )}
            />

            <ShoppingCart.PriceSummaryRow
                title={
                    <span className="font-bold">
                        <Trans>Total</Trans>
                    </span>
                }
                price={formatCurrency(
                    total,
                    transportProduct?.price?.currency ?? "EUR"
                )}
            />
        </ShoppingCart.Root>
    );
}
